import { z } from 'zod';

export type MemberFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  smsBookingNotifications: boolean;
  smsRecallProposals: boolean;
  isAdmin: boolean;
  /** Employee of a leasing company or car dealership. Binary stand-in for the membership role. */
  isEmployee: boolean;
  isPrimaryContact: boolean;
};

const REQUIRED_TEXT = z.string().trim().min(1);

/**
 * Only the fields this viewer may save are validated. Both halves of the sheet
 * share one Save, so requiring a name or email the viewer cannot even edit —
 * an imported member can arrive without a last name — would leave them unable
 * to change their own notification settings, with nothing they could fix.
 */
export const buildMemberSchema = ({ canEditAccount }: { canEditAccount: boolean }) =>
  z.object({
    firstName: canEditAccount ? REQUIRED_TEXT : z.string(),
    lastName: canEditAccount ? REQUIRED_TEXT : z.string(),
    email: canEditAccount ? z.union([z.literal(''), z.email()]) : z.string(),
    phoneNumber: canEditAccount ? REQUIRED_TEXT : z.string(),
    smsBookingNotifications: z.boolean(),
    smsRecallProposals: z.boolean(),
    isAdmin: z.boolean(),
    isEmployee: z.boolean(),
    isPrimaryContact: z.boolean()
  });

/**
 * The two halves save through different endpoints, so the form has to know which
 * half the user touched. Listing the fields once keeps the defaults and the
 * changed-check from drifting apart when a field is added.
 *
 * The account fields are the person's alone (staff aside), never a group admin's to edit.
 */
export const ACCOUNT_FIELDS = [
  'firstName',
  'lastName',
  'phoneNumber',
  'email'
] as const satisfies readonly (keyof MemberFormValues)[];
export const NOTIFICATION_FIELDS = [
  'smsBookingNotifications',
  'smsRecallProposals'
] as const satisfies readonly (keyof MemberFormValues)[];
/** The group role — same endpoint as the notifications, different permission. */
export const ROLE_FIELDS = ['isAdmin'] as const satisfies readonly (keyof MemberFormValues)[];
/**
 * Employee vs ordinary member of a leasing company or car dealership. Kept apart from
 * `ROLE_FIELDS`: turning the admin switch on forces employee, and demoting an admin must not.
 */
export const MEMBERSHIP_ROLE_FIELDS = ['isEmployee'] as const satisfies readonly (keyof MemberFormValues)[];
/**
 * Admin-only for the backend too, but kept apart from `ROLE_FIELDS`: the last
 * admin cannot be demoted, and that rule must not also freeze this field.
 */
export const PRIMARY_CONTACT_FIELDS = ['isPrimaryContact'] as const satisfies readonly (keyof MemberFormValues)[];
